import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { EMPTY, of } from 'rxjs';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { ConfirmationDialogService } from 'src/app/common-components/confirmation-dialog/confirmation-dialog.service';
import { DialogSize } from 'src/app/common-components/confirmation-dialog/enumerations/dialog-size.enum';
import { LcsDialogService } from 'src/app/common-components/dialog/dialog.service';
import { AllowBannedIpDialogComponent } from '../dialogs/allow-banned-ip-dialog.component';
import {
   AllowBannedIpDialogData,
   AllowBannedIpDialogResult,
   getOriginatingType,
} from '../models/allow-banned-ip.model';
import { BannedProvisioningIpFilterOption } from '../models/banned-provisioning-ip-filter-option';
import {
   BannedProvisioningIpOrderingOptions,
   BannedProvisioningIpRegisterOptions,
} from '../models/banned-provisioning-ip-register-options';
import { BannedProvisioningIpRegisterResults } from '../models/banned-provisioning-ip-register-results.model';
import { GetBannedProvisioningIps } from '../queries/generated/GetBannedProvisioningIps';
import { BannedProvisioningIpRegisterApiService } from '../services/banned-provisioning-ip-register-api.service';
import {
   allowIp,
   allowIpFailure,
   allowIpSuccess,
   bannedProvisioningIpRegisterAdapterActions,
   refreshList,
   unbanIp,
   unbanIpFailure,
   unbanIpSuccess,
} from './banned-provisioning-ip-register.actions';
import {
   BannedProvisioningIpRegisterState,
   selectEndCursor,
   selectOrderingOptions,
   selectQuickFilters,
} from './banned-provisioning-ip-register.reducer';
import { selectFilters } from './banned-provisioning-ip-register.selectors';

@Injectable()
export class BannedProvisioningIpRegisterEffects {
   loadBannedIpList$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(bannedProvisioningIpRegisterAdapterActions.pageEnter),
         withLatestFrom(
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectFilters)),
            this.store.pipe(select(selectOrderingOptions))
         ),
         switchMap(([action, quickFilters, filters, orderingOptions]) => {
            return this.loadNewData(quickFilters, filters, orderingOptions);
         })
      );
   });

   refreshList$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(refreshList, unbanIpSuccess, allowIpSuccess),
         withLatestFrom(
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectFilters)),
            this.store.pipe(select(selectOrderingOptions))
         ),
         switchMap(([action, quickFilters, filters, orderingOptions]) => {
            return this.loadNewData(quickFilters, filters, orderingOptions);
         })
      );
   });

   loadBannedIpPage$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(bannedProvisioningIpRegisterAdapterActions.loadPage),
         withLatestFrom(
            this.store.pipe(select(selectFilters)),
            this.store.pipe(select(selectEndCursor)),
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectOrderingOptions))
         ),
         switchMap(([action, filters, endCursor, quickFilters, orderingOptions]) => {
            return this.loadNewPage(endCursor, filters, quickFilters, orderingOptions);
         })
      );
   });

   quickFilterChanged$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(bannedProvisioningIpRegisterAdapterActions.quickFilterChanged),
         withLatestFrom(
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectFilters)),
            this.store.pipe(select(selectOrderingOptions))
         ),
         switchMap(([action, quickFilters, filters, orderingOptions]) => {
            return this.loadNewData(quickFilters, filters, orderingOptions);
         })
      );
   });

   orderingOptionsChanged$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(bannedProvisioningIpRegisterAdapterActions.orderingOptionsChanged),
         withLatestFrom(
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectFilters)),
            this.store.pipe(select(selectOrderingOptions))
         ),
         switchMap(([action, quickfilters, filters, orderingOptions]) => {
            return this.loadNewData(quickfilters, filters, orderingOptions);
         })
      );
   });

   allowIp$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(allowIp),
         switchMap((action) => {
            const dialogRef = this.dialogService.openFormComponent<
               AllowBannedIpDialogComponent,
               AllowBannedIpDialogData
            >(AllowBannedIpDialogComponent, {
               data: { ipAddress: action.ipAddress },
               panelClass: ['add-dialog-panel', 'dialog-container'],
            });

            return dialogRef.afterClosed().pipe(
               switchMap((allowedIpAddress: AllowBannedIpDialogResult | undefined) => {
                  if (!allowedIpAddress) {
                     return EMPTY;
                  }

                  return this.bannedProvisioningIpRegisterApiService
                     .allowIpAddress({ ...allowedIpAddress, originatingType: getOriginatingType(action.blockedIpType) })
                     .pipe(
                        map(() => {
                           return allowIpSuccess({ ipAddress: action.ipAddress });
                        }),
                        catchError((error) => {
                           return of(allowIpFailure(error));
                        })
                     );
               })
            );
         })
      );
   });

   allowIpSuccess$ = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(allowIpSuccess),
            tap((data) => {
               this.snackBar.open(`${data.ipAddress} allowed and moved to Unbanning`);
            })
         );
      },
      { dispatch: false }
   );

   unbanIp$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(unbanIp),
         switchMap((action) => {
            const message = `Are you sure you would like to unban ${action.ipAddress}?`;

            const dialogRef = this.confirmationService.confirm({
               data: {
                  header: 'Unban IP?',
                  message,
               },
               size: DialogSize.Medium,
            });

            return dialogRef.afterClosed().pipe(
               switchMap((confirmed) => {
                  if (confirmed) {
                     return this.bannedProvisioningIpRegisterApiService
                        .unbanBlockedIpAddress(action.blockedIpAddressId)
                        .pipe(
                           map(() => {
                              return unbanIpSuccess({ ipAddress: action.ipAddress });
                           }),
                           catchError((error) => {
                              return of(unbanIpFailure(error));
                           })
                        );
                  }
                  return EMPTY;
               })
            );
         })
      );
   });

   unbanIpSuccess$ = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(unbanIpSuccess),
            tap((data) => {
               this.snackBar.open(`${data.ipAddress} moved to Unbanning`);
            })
         );
      },
      { dispatch: false }
   );

   constructor(
      private actions$: Actions,
      private store: Store<BannedProvisioningIpRegisterState>,
      private bannedProvisioningIpRegisterApiService: BannedProvisioningIpRegisterApiService,
      private confirmationService: ConfirmationDialogService,
      private dialogService: LcsDialogService,
      private snackBar: MatSnackBar
   ) {}

   private loadNewData(
      quickFilters: BannedProvisioningIpRegisterOptions | null = null,
      filters: BannedProvisioningIpFilterOption[] | null = null,
      orderingOptions: BannedProvisioningIpOrderingOptions[] | null = null
   ) {
      return this.bannedProvisioningIpRegisterApiService
         .getBannedProvisioningIps(null, filters, quickFilters, orderingOptions)
         .pipe(
            map((result) => {
               return bannedProvisioningIpRegisterAdapterActions.loadSuccess(
                  this.convertGetBannedIpsResultToLoadSuccessProps(result.data)
               );
            }),
            catchError((error) => {
               return of(bannedProvisioningIpRegisterAdapterActions.loadFailure(error));
            })
         );
   }

   private loadNewPage(
      endCursor: string | null,
      filters: BannedProvisioningIpFilterOption[],
      quickFilters: BannedProvisioningIpRegisterOptions | null = null,
      orderingOptions: BannedProvisioningIpOrderingOptions[] | null = null
   ) {
      return this.bannedProvisioningIpRegisterApiService
         .getBannedProvisioningIps(endCursor, filters, quickFilters, orderingOptions)
         .pipe(
            map((result) => {
               return bannedProvisioningIpRegisterAdapterActions.loadPageSuccess(
                  this.convertGetBannedIpsResultToLoadSuccessProps(result.data)
               );
            }),
            catchError((error) => {
               return of(bannedProvisioningIpRegisterAdapterActions.loadPageFailure(error));
            })
         );
   }

   private convertGetBannedIpsResultToLoadSuccessProps(
      getBannedIps: GetBannedProvisioningIps
   ): BannedProvisioningIpRegisterResults {
      const unfilteredCount = getBannedIps.unfilteredBlockedIpAddressesConnection.totalCount;

      return {
         endCursor: getBannedIps.blockedIpAddressesConnection.pageInfo.endCursor,
         hasNextPage: getBannedIps.blockedIpAddressesConnection.pageInfo.hasNextPage,
         items: getBannedIps.blockedIpAddressesConnection.items,
         totalCount: unfilteredCount,
      };
   }
}
